import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../../../authContext';
import { useSecurity } from '../../../../../SecurityContext';
import { fetchMemberProfileCard } from '../../../../../components/tools/memberProfileCardApi';
import { fetchRosterFile } from '../../rosterTools/api/rosterFileApi';
import { rosterLastSeen } from '../../rosterTools/api/rosterApi';
import { mergeRosterAndTraining } from '../../rosterTools/data/rosterData';
import useRosterFileSocket from '../../rosterTools/hooks/useRosterFileSocket';
import { pickPrimaryHandleIdFromHandleList, linkedHandlesIncludeMaster } from '../dashboard/dashboardUtils';
import {
  getPersonalHubSessionBoot,
  personalHubSessionCacheKey,
  setPersonalHubSessionCache,
  PERSONAL_HUB_SECTION_DASHBOARD,
} from '../tools/personalHubSessionCache';
import { personalHubViewerCacheKey } from '../tools/personalHubViewerCacheKey';

export default function usePersonalHubDashboardProfile() {
  const { handles, isLoggedIn, userId } = useAuth();
  const { isViewAsActive, viewAsUserId } = useSecurity();

  const viewerKey = personalHubViewerCacheKey({ userId, isViewAsActive, viewAsUserId });
  const cacheKey = personalHubSessionCacheKey(PERSONAL_HUB_SECTION_DASHBOARD, viewerKey);
  const sessionBoot = getPersonalHubSessionBoot(cacheKey);

  const [player, setPlayer] = useState(() => sessionBoot?.player ?? null);
  const [loading, setLoading] = useState(() => !sessionBoot);
  const [pageFetching, setPageFetching] = useState(false);
  const [error, setError] = useState(sessionBoot?.error ?? null);
  const [missingHandle, setMissingHandle] = useState(sessionBoot?.missingHandle ?? false);
  const [dashboardHandleId, setDashboardHandleId] = useState(() => {
    if (sessionBoot?.dashboardHandleId) return sessionBoot.dashboardHandleId;
    if (!isViewAsActive) return pickPrimaryHandleIdFromHandleList(handles);
    return null;
  });
  const [dashboardClusterScope, setDashboardClusterScope] = useState(
    () => sessionBoot?.dashboardClusterScope ?? linkedHandlesIncludeMaster(handles),
  );
  const [shellVisible, setShellVisible] = useState(() => !!sessionBoot);

  const hasLoadedOnceRef = useRef(!!sessionBoot);
  const handleIdRef = useRef(dashboardHandleId);
  handleIdRef.current = dashboardHandleId;

  const persistCache = useCallback(
    (payload) => {
      setPersonalHubSessionCache(cacheKey, {
        player: payload.player ?? null,
        dashboardHandleId: payload.dashboardHandleId ?? null,
        dashboardClusterScope: payload.dashboardClusterScope === true,
        missingHandle: payload.missingHandle === true,
        error: payload.error ?? null,
      });
    },
    [cacheKey]
  );

  const loadProfile = useCallback(
    async ({ silent = false } = {}) => {
      if (!isLoggedIn) {
        setPlayer(null);
        setLoading(false);
        setPageFetching(false);
        setError(null);
        setMissingHandle(false);
        setDashboardHandleId(null);
        setDashboardClusterScope(false);
        setShellVisible(false);
        return null;
      }

      const hasCache = !!getPersonalHubSessionBoot(cacheKey);
      const isBlankVisit = !hasLoadedOnceRef.current && !hasCache;

      if (isBlankVisit) {
        setLoading(true);
      } else if (!silent) {
        setPageFetching(true);
      }
      setError(null);
      setMissingHandle(false);

      let handleId = null;
      let clusterScope = false;

      if (isViewAsActive && viewAsUserId != null) {
        const cardRes = await fetchMemberProfileCard(viewAsUserId);
        if (!cardRes?.ok) {
          setPlayer(null);
          setError(cardRes?.error || 'Could not load member profile.');
          setDashboardHandleId(null);
          setDashboardClusterScope(false);
          setShellVisible(!!getPersonalHubSessionBoot(cacheKey));
          return null;
        }
        handleId = pickPrimaryHandleIdFromHandleList(cardRes.card?.handles);
        clusterScope = linkedHandlesIncludeMaster(cardRes.card?.handles, { isViewAsActive: true });
      } else {
        handleId = pickPrimaryHandleIdFromHandleList(handles);
        clusterScope = linkedHandlesIncludeMaster(handles);
      }

      if (!handleId) {
        setPlayer(null);
        setMissingHandle(true);
        setDashboardHandleId(null);
        setDashboardClusterScope(false);
        setShellVisible(true);
        persistCache({
          player: null,
          dashboardHandleId: null,
          dashboardClusterScope: false,
          missingHandle: true,
          error: null,
        });
        return null;
      }

      const data = await fetchRosterFile(handleId);
      if (!data?.ok) {
        setPlayer(null);
        setError(data?.error || 'Could not load profile.');
        setDashboardHandleId(null);
        setDashboardClusterScope(false);
        setShellVisible(!!getPersonalHubSessionBoot(cacheKey));
        return null;
      }

      const trainingsByHandleId = {
        [String(handleId)]: data.player?.trainings || [],
      };
      const merged = mergeRosterAndTraining([data.player], trainingsByHandleId);
      const nextPlayer = merged[0] || null;

      setPlayer(nextPlayer);
      setDashboardHandleId(handleId);
      setDashboardClusterScope(clusterScope);
      setShellVisible(true);
      hasLoadedOnceRef.current = true;

      persistCache({
        player: nextPlayer,
        dashboardHandleId: handleId,
        dashboardClusterScope: clusterScope,
        missingHandle: false,
        error: null,
      });

      const maxSeq = Number(data.rosterWsMaxSeq || 0);
      if (maxSeq > 0) {
        await rosterLastSeen(maxSeq).catch(() => {});
      }

      return maxSeq;
    },
    [cacheKey, handles, isLoggedIn, isViewAsActive, persistCache, viewAsUserId]
  );

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        const hasCache = !!getPersonalHubSessionBoot(cacheKey);
        await loadProfile({ silent: hasCache });
      } catch (e) {
        if (cancelled) return;
        setPlayer(null);
        setError(e?.message || 'Could not load profile.');
        setDashboardHandleId(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setPageFetching(false);
        }
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [cacheKey, loadProfile]);

  useEffect(() => {
    hasLoadedOnceRef.current = !!getPersonalHubSessionBoot(cacheKey);
  }, [cacheKey]);

  const refetchProfile = useCallback(
    async ({ silent = true } = {}) => {
      try {
        if (!silent && !getPersonalHubSessionBoot(cacheKey)) {
          setLoading(true);
        } else if (!silent) {
          setPageFetching(true);
        }
        return await loadProfile({ silent: true });
      } catch (err) {
        console.error('[PersonalHub:dashboard] refetch failed:', err);
        return null;
      } finally {
        setLoading(false);
        setPageFetching(false);
      }
    },
    [cacheKey, loadProfile]
  );

  const patchPlayer = useCallback((patch) => {
    if (!patch || typeof patch !== 'object') return;
    setPlayer((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      const boot = getPersonalHubSessionBoot(cacheKey);
      persistCache({
        player: next,
        dashboardHandleId: boot?.dashboardHandleId ?? handleIdRef.current,
        dashboardClusterScope: boot?.dashboardClusterScope === true,
        missingHandle: false,
        error: null,
      });
      return next;
    });
  }, [cacheKey, persistCache]);

  useRosterFileSocket({
    enabled: isLoggedIn && Boolean(dashboardHandleId),
    isLoggedIn,
    refetch: refetchProfile,
  });

  return {
    player,
    loading,
    pageFetching,
    shellVisible,
    error,
    missingHandle,
    dashboardHandleId,
    dashboardClusterScope,
    patchPlayer,
  };
}
